import { taxRatesQuery } from "@common/api/tax-rates"
import { useHasPermission } from "@common/hooks/use-permissions"
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router"
import { z } from "zod"
import {
  type Display,
  DisplayOptions,
} from "@/components/layout/display-options"
import { FilterBar } from "@/components/layout/filter-bar"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { requirePermission } from "@/lib/require-permission"
import { NewTaxRateButton } from "./-components/new-tax-rate-button"
import { TaxRateTable } from "./-components/tax-rate-table"
import {
  GROUPING_LABELS,
  GROUPINGS,
  type Grouping,
  groupingSchema,
} from "./-lib/grouping"

const filterSchema = z.object({
  search: z.string().optional().catch(""),
  archived: z.boolean().optional().catch(false),
  group: groupingSchema.optional().catch(undefined),
  subgroup: groupingSchema.optional().catch(undefined),
})

export const Route = createFileRoute(
  "/_layout/settings/_panel/quoting/tax-rates",
)({
  staticData: {
    breadcrumb: [
      { label: "Quoting - Tax rates", href: "/settings/quoting/tax-rates" },
    ],
  },
  validateSearch: (search) => filterSchema.parse(search),
  loaderDeps: ({ search }) => ({
    search: search.search ?? "",
    archived: search.archived ?? false,
  }),
  beforeLoad: ({ context: { queryClient } }) =>
    requirePermission(
      queryClient,
      "tax_rate:read",
      "/settings/workspace/general",
    ),
  loader: ({ context: { queryClient }, deps }) =>
    queryClient.ensureQueryData(
      taxRatesQuery({ search: deps.search, deleted: deps.archived }),
    ),
  component: TaxRatesPage,
})

function TaxRatesPage(): React.ReactElement {
  const { search = "", archived = false, group, subgroup } = Route.useSearch()
  const display = {
    group,
    subgroup: group && subgroup !== group ? subgroup : undefined,
  }

  return (
    <div className="flex h-full flex-col">
      <FilterBar>
        <Toolbar search={search} archived={archived} display={display} />
      </FilterBar>
      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-y-auto [--filter-bar-height:0px]">
          <TaxRateTable search={search} archived={archived} display={display} />
        </div>
        <Outlet />
      </div>
    </div>
  )
}

function Toolbar({
  search,
  archived,
  display,
}: {
  search: string
  archived: boolean
  display: Display<Grouping>
}): React.ReactElement {
  const navigate = useNavigate()
  const canCreate = useHasPermission("tax_rate:create")
  const filter = (next: string) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, search: next || undefined }),
      replace: true,
    })
  const showArchived = (next: boolean) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, archived: next || undefined }),
      replace: true,
    })
  const changeDisplay = (next: Display<Grouping>) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...next }),
      replace: true,
    })

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={["active", "archived"] as const}
          tab={archived ? "archived" : "active"}
          label={(tab) => (tab === "active" ? "Active" : "Archived")}
          name="Record status"
          onSelect={(tab) => showArchived(tab === "archived")}
        />
      }
      actions={
        <>
          <NameFilter
            search={search}
            onSearch={filter}
            placeholder="Filter by code or name…"
          />
          <DisplayOptions
            groupings={GROUPINGS}
            labels={GROUPING_LABELS}
            {...display}
            onChange={changeDisplay}
          />
          {canCreate && <NewTaxRateButton />}
        </>
      }
    />
  )
}
